import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermission } from "@/contexts/AuthContext";
import { deleteThread } from "../api/rag-api.js";
import { refreshSupervisorChats } from "@/apps/chat/supervisor-chat-nav";
import { apiFetch } from "@/lib/api/client";
import { useRefreshRagNav } from "./RagNavRefreshContext.js";
import type { RagNavSubItem } from "../rag-nav.js";

function getChatListPath(chatPath: string): string {
  const lastSlash = chatPath.lastIndexOf("/");
  return lastSlash > 0 ? chatPath.slice(0, lastSlash) : chatPath;
}

export function useSidebarDeleteChat(activeAppId: string | undefined) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const refreshRagNav = useRefreshRagNav();
  const canDeleteKmChat = usePermission("kmChat", "delete");
  const [pendingDelete, setPendingDelete] = useState<RagNavSubItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canDeleteChat =
    activeAppId === "regulatory" || activeAppId === "chat"
      ? true
      : activeAppId === "rag"
        ? canDeleteKmChat
        : false;

  const requestDeleteChat = useCallback(
    (item: RagNavSubItem) => {
      if (!canDeleteChat || deletingId) return;
      setPendingDelete(item);
    },
    [canDeleteChat, deletingId]
  );

  const cancelDeleteChat = useCallback(() => {
    if (deletingId) return;
    setPendingDelete(null);
  }, [deletingId]);

  const confirmDeleteChat = useCallback(async () => {
    if (!canDeleteChat || !pendingDelete || deletingId) return;

    const item = pendingDelete;
    setDeletingId(item.id);
    try {
      if (activeAppId === "chat") {
        await apiFetch(`/api/supervisor-chat/conversations/${item.id}`, { method: "DELETE" });
        if (pathname === item.to) navigate("/chat");
        refreshSupervisorChats();
      } else {
        await deleteThread(item.id);

        if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
          navigate(getChatListPath(item.to));
        }

        refreshRagNav();
      }
      setPendingDelete(null);
    } finally {
      setDeletingId(null);
    }
  }, [activeAppId, canDeleteChat, deletingId, navigate, pathname, pendingDelete, refreshRagNav]);

  return {
    requestDeleteChat,
    confirmDeleteChat,
    cancelDeleteChat,
    pendingDelete,
    deletingId,
    canDeleteChat,
  };
}
